import type { AnswerLayoutResult, ArtifactResult } from "~/services/aiOrchestrator";

import { isHtmlArtifact } from "~/helpers/aiChat/reportArtifacts";
import coerceCellNumber from "~/helpers/grid/coerceCellNumber";
import formatLocaleNumber from "~/helpers/localeNumberFormat";
import { isValidDateInFormat, STRICT_ISO_DATE, stringFormatDate } from "~/helpers/timestampFormat";

const DRAWABLE_VERSION = 1;
const KNOWN_COLUMN_TYPES = new Set(["DATE", "NUMBER", "TEXT"]);
const MAX_ROWS_BEFORE_SCROLL = 10;
const DECIMALS = /\.(\d+)$/;

export interface AnswerLayoutRow {
	cells: string[];
	id: string;
}

const canDraw = (layout: AnswerLayoutResult): boolean =>
	layout.version === DRAWABLE_VERSION &&
	layout.sections.every((section) => section.columns.every((column) => KNOWN_COLUMN_TYPES.has(column.type)));

const drawable = (layouts: AnswerLayoutResult[] | undefined): AnswerLayoutResult[] =>
	layouts?.every(canDraw) ? layouts : [];

const reportArtifactsToShow = <T extends Pick<ArtifactResult, "mimeType">>(
	artifacts: T[] | undefined,
	layouts: AnswerLayoutResult[] | undefined
): T[] | undefined =>
	drawable(layouts).length > 0 ? artifacts?.filter((artifact) => !isHtmlArtifact(artifact)) : artifacts;

const compareOrdinal = <T>(a: T, b: T): number => {
	if (a < b) return -1;
	return a > b ? 1 : 0;
};

const compareCells = (type: string, a: string, b: string): number => {
	if (type === "NUMBER") return compareOrdinal(coerceCellNumber(a) ?? -Infinity, coerceCellNumber(b) ?? -Infinity);
	if (type === "DATE") return compareOrdinal(a, b);
	return a.localeCompare(b);
};

const formatNumberCell = (raw: string): string => {
	const value = coerceCellNumber(raw);
	if (value === null) return raw;
	const decimals = DECIMALS.exec(raw)?.[1].length ?? 0;
	return formatLocaleNumber(value, { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
};

const formatCell = (type: string, raw: string): string => {
	if (type === "NUMBER") return formatNumberCell(raw);
	if (type === "DATE" && isValidDateInFormat(raw, STRICT_ISO_DATE)) return stringFormatDate(raw);
	return raw;
};

const scrollsInside = (rowCount: number): boolean => rowCount > MAX_ROWS_BEFORE_SCROLL;

const answerLayouts = {
	compareCells,
	drawable,
	formatCell,
	reportArtifactsToShow,
	scrollsInside
};

export default answerLayouts;
